import InventoryIcon from '@mui/icons-material/Inventory';
import { Box, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import formatPrxSalesRep, { PRX_SALES_REP_LABEL } from 'common/utils/formatPrxSalesRep';

const InfoRow = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant='caption' color='text.secondary' display='block'>
      {label}
    </Typography>
    <Typography variant='body2'>{value || '—'}</Typography>
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

InfoRow.defaultProps = { value: null };

const OrderDetailsPanel = ({ order, initialFulfillmentAddress, orderAddress }) => {
  const shippingOptions = [
    ...new Set((order.lineItems || []).map((li) => li.shippingOption).filter(Boolean)),
  ].map((opt) =>
    opt
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );

  return (
    <Paper variant='outlined' sx={{ flex: 3, p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <InventoryIcon color='primary' fontSize='small' />
        <Typography variant='subtitle1' fontWeight='bold'>
          Order Details
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow
            label='Created'
            value={order.createdAt ? format(new Date(order.createdAt), 'MM/dd/yyyy') : null}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow
            label='Last Updated'
            value={order.lastUpdated ? format(new Date(order.lastUpdated), 'MM/dd/yyyy') : null}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {shippingOptions.length > 0 && (
            <InfoRow label='Shipping' value={shippingOptions.join(', ')} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* POM-6494: '—' until the enriched order (which carries it) has loaded. */}
          <InfoRow label={PRX_SALES_REP_LABEL} value={formatPrxSalesRep(order.prxSalesRep)} />
        </Box>
      </Box>

      {(initialFulfillmentAddress || orderAddress) && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {initialFulfillmentAddress && (
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                Initial Fulfillment Address
              </Typography>
              {[
                initialFulfillmentAddress.addressLine1 || initialFulfillmentAddress.line1,
                initialFulfillmentAddress.addressLine2 || initialFulfillmentAddress.line2,
                [
                  initialFulfillmentAddress.addressCity || initialFulfillmentAddress.city,
                  initialFulfillmentAddress.addressState || initialFulfillmentAddress.state,
                  initialFulfillmentAddress.addressZip || initialFulfillmentAddress.zip,
                ]
                  .filter(Boolean)
                  .join(', '),
              ]
                .filter(Boolean)
                .map((line) => (
                  <Typography key={line} variant='body2'>
                    {line}
                  </Typography>
                ))}
            </Box>
          )}

          {orderAddress && (
            <Box
              sx={{
                flex: 1,
                minWidth: 160,
                pl: 1.5,
                border: 1,
                borderColor: 'grey.500',
                borderLeft: 5,
                borderLeftColor: 'primary.main',
                borderRadius: 1,
              }}
            >
              <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                Current Fulfillment Address
              </Typography>
              {[
                orderAddress.line1,
                orderAddress.line2,
                [orderAddress.city, orderAddress.state, orderAddress.zip]
                  .filter(Boolean)
                  .join(', '),
              ]
                .filter(Boolean)
                .map((line) => (
                  <Typography key={line} variant='body2' fontWeight='medium'>
                    {line}
                  </Typography>
                ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

OrderDetailsPanel.propTypes = {
  order: PropTypes.shape({
    createdAt: PropTypes.string,
    lastUpdated: PropTypes.string,
    lineItems: PropTypes.arrayOf(PropTypes.shape({})),
    prxSalesRep: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      unavailable: PropTypes.bool,
    }),
  }).isRequired,
  initialFulfillmentAddress: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressCity: PropTypes.string,
    addressState: PropTypes.string,
    addressZip: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
  orderAddress: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
};

OrderDetailsPanel.defaultProps = {
  initialFulfillmentAddress: null,
  orderAddress: null,
};

export default OrderDetailsPanel;
